import React, { useMemo, useState, useEffect } from "react";
import { Box, Typography, TextField, Button, Alert } from "@mui/material";
import { styled } from "@mui/material/styles";
import { useSearchParams, useNavigate } from "react-router-dom";
import userManagementService from "@services/UserManagement";
import { decryptInvitationParams } from "@utils/simpleDecryption";
import { isStandardHubUserTypeName } from "@utils/userUtils";
import ContentUsBgImage from "@assets/ContentUsBgImage.png";

// Container wrappers (mirror Login/Signup structure)
const SignUpContainer = styled(Box)(({ theme }) => ({
  backgroundColor: "#ffffff",
//   width: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  position: "relative",
  padding: "16px",
  [theme.breakpoints.down("sm")]: {
    padding: "8px",
    alignItems: "flex-start",
    paddingTop: "20px",
  },
}));

const ContactSection = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "24px 0px",
  position: "relative",
  width: "100%",
  maxWidth: "900px",
  margin: "0 auto",
  gap: 16,
  [theme.breakpoints.down("lg")]: {
    gap: 12,
    flexDirection: "column",
    padding: "12px",
    maxWidth: "900px",
  },
  [theme.breakpoints.down("md")]: {
    flexDirection: "column",
    padding: "16px 8px",
    gap: 10,
    maxWidth: "100%",
  },
  [theme.breakpoints.down("sm")]: {
    padding: "12px 6px",
    gap: 8,
  },
}));

const LeftPanel = styled(Box)(({ theme }) => ({
  position: "relative",
  width: "620px",
  height: "520px",
  backgroundImage: `url(${ContentUsBgImage})`,
  backgroundSize: "cover",
  backgroundPosition: "center",
  backgroundRepeat: "no-repeat",
  borderRadius: "32px",
  border: "1px solid #ffffff",
  overflow: "hidden",
  marginRight: "-80px",
  flexShrink: 0,
  [theme.breakpoints.down("lg")]: {
    width: "480px",
    height: "400px",
    marginRight: "-20px",
  },
  [theme.breakpoints.down("md")]: {
    width: "100%",
    maxWidth: "500px",
    height: "auto",
    minHeight: "240px",
    aspectRatio: "16 / 10",
    marginRight: 0,
    borderRadius: "24px",
  },
  [theme.breakpoints.down("sm")]: {
    maxWidth: "100%",
    minHeight: "240px",
    borderRadius: "16px",
  },
}));

const LeftPanelContent = styled(Box)(({ theme }) => ({
  position: "absolute",
  left: "95.5px",
  top: "101px",
  width: "544px",
  color: "#ffffff",
  [theme.breakpoints.down("lg")]: {
    left: "32px",
    top: "48px",
    width: "380px",
  },
  [theme.breakpoints.down("md")]: {
    position: "relative",
    left: 0,
    top: 0,
    width: "100%",
    padding: "16px 12px 12px",
    textAlign: "center",
  },
  [theme.breakpoints.down("sm")]: {
    padding: "18px 0px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
  },
}));

const MainTitle = styled(Typography)(({ theme }) => ({
  fontFamily: "Rethink Sans, sans-serif",
  fontWeight: 600,
  fontSize: "64px",
  lineHeight: "1.1",
  marginBottom: "40px",
  color: "#ffffff",
  [theme.breakpoints.down("lg")]: {
    fontSize: "42px",
    marginBottom: "24px",
  },
  [theme.breakpoints.down("md")]: {
    fontSize: "36px",
    marginBottom: "12px",
    lineHeight: "1.2",
  },
  [theme.breakpoints.down("sm")]: {
    fontSize: "28px",
    marginBottom: "12px",
    paddingTop: "48px",
    textAlign: "center",
  },
}));

const SubTitle = styled(Typography)(({ theme }) => ({
  fontFamily: "Rethink Sans, sans-serif",
  fontWeight: 500,
  fontSize: "18px",
  lineHeight: "1.5",
  color: "#ffffff",
  width: "422px",
  textAlign: "center",
  whiteSpace: "normal",
  overflowWrap: "normal",
  wordBreak: "normal",
  hyphens: "none",
  [theme.breakpoints.down("lg")]: {
    width: "360px",
  },
  [theme.breakpoints.down("md")]: {
    width: "100%",
    fontSize: "16px",
    lineHeight: "1.45",
  },
  [theme.breakpoints.down("sm")]: {
    fontSize: "14px",
    lineHeight: "1.4",
    maxWidth: 300,
    margin: "0 auto",
    textAlign: "center",
    whiteSpace: "normal",
    overflowWrap: "normal",
    wordBreak: "normal",
    hyphens: "none",
  },
}));

// Right card mirrors Login RightPanel
const RightPanel = styled(Box)(({ theme }) => ({
  backgroundColor: "#f4feff",
  border: "1px solid #05495a",
  borderRadius: "16px",
  padding: "32px 28px 32px 32px",
  width: "480px",
  position: "relative",
  flexShrink: 0,
  minHeight: 500,
  [theme.breakpoints.down("lg")]: {
    width: "420px",
    padding: "28px 24px 28px 28px",
    minHeight: 540,
  },
  [theme.breakpoints.down("md")]: {
    width: "100%",
    maxWidth: "480px",
    padding: "24px 18px 24px 24px",
    minHeight: 520,
  },
  [theme.breakpoints.down("sm")]: {
    padding: "20px 14px 20px 20px",
    borderRadius: "12px",
    margin: "0 6px",
    maxWidth: "90%",
    minHeight: 500,
  },
}));

const Title = styled(Typography)({
  fontFamily: "Rethink Sans, sans-serif",
  fontWeight: 700,
  fontSize: 28,
  lineHeight: "35px",
  color: "#033843",
  marginBottom: 16,
});

// Match Login form styles
const FormContainer = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: "20px",
  width: "100%",
});

const InputContainer = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: "4.5px",
  width: "100%",
});

const InputLabel = styled(Typography)({
  fontFamily: "Rethink Sans, sans-serif",
  fontWeight: 600,
  fontSize: "14px",
  lineHeight: "20px",
  color: "#262626",
});

const StyledTextField = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    backgroundColor: "#ffffff",
    height: "42px",
    borderRadius: "4px",
    "& fieldset": {
      borderColor: "#e6fafc",
    },
    "&:hover fieldset": {
      borderColor: "#bfe5ec",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#0a829d",
    },
    boxShadow:
      "inset 0px 0px 8px 0px rgba(0,108,116,0.12), inset 0px 0px 4px 0px rgba(34,110,127,0.25)",
  },
  "& .MuiOutlinedInput-input": {
    padding: "11px 14px",
    fontSize: "14px",
    fontFamily: "Rethink Sans, sans-serif",
    [theme.breakpoints.down("sm")]: {
      fontSize: "16px",
      padding: "12px 14px",
    },
  },
  [theme.breakpoints.down("sm")]: {
    "& .MuiOutlinedInput-root": {
      height: "48px",
      minHeight: "44px",
    },
  },
}));

const TermsContainer = styled(Box)({
  padding: "8px",
  display: "flex",
  flexDirection: "column",
  gap: "4.5px",
  alignItems: "center",
  width: "100%",
});

const TermsText = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "5px",
  fontFamily: "Rethink Sans, sans-serif",
  fontWeight: 600,
  fontSize: "14px",
  lineHeight: "20px",
});

const TermsLink = styled("span")({
  color: "#0a829d",
  textDecoration: "underline",
  cursor: "pointer",
  "&:hover": {
    color: "#086e86",
  },
});

export default function ConfirmInvitation() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [decryptedToken, setDecryptedToken] = useState<string | null>(null);
  const [decryptionReady, setDecryptionReady] = useState(false);
  const [userType, setUserType] = useState<string | null>(null);
  const tokenFromUrl = useMemo(() => {
    return (
      params.get("confirmation_token") ||
      params.get("token") ||
      params.get("confirmationToken") ||
      ""
    ).replace(/^"|"$/g, "");
  }, [params]);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordHint, setPasswordHint] = useState<string | null>(null);
  const [confirmPasswordError, setConfirmPasswordError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // Decrypt data param after state is initialized
  useEffect(() => {
    const dataParam = params.get("data") || "";
    if (!dataParam) {
      setDecryptionReady(true);
      return;
    }
    (async () => {
      try {
        const res = await decryptInvitationParams(dataParam);
        const t = res?.confirmation_token || res?.token;
        if (t) setDecryptedToken(String(t));
        const UserType = String(
          res?.user_type || res?.userType || res?.type || "",
        );
        if (UserType) {
          setUserType(UserType);
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error("[confirm-invitation] simple decrypt failed", e);
      } finally {
        setDecryptionReady(true);
      }
    })();
  }, [params]);

  const validatePassword = (pwd: string): string | null => {
    // Same requirements as reset password (example policy):
    // - at least 8 chars, includes uppercase, lowercase, number, and special char
    const errors: string[] = [];
    if (pwd.length < 8) errors.push("at least 8 characters");
    if (!/[A-Z]/.test(pwd)) errors.push("one uppercase letter");
    if (!/[a-z]/.test(pwd)) errors.push("one lowercase letter");
    if (!/[0-9]/.test(pwd)) errors.push("one number");
    if (!/[!@#$%^&*(),.?"{}|<>_\-[\]\\/+=~`]/.test(pwd)) errors.push("one special character");
    if (errors.length) return `Password must contain ${errors.join(", ")}.`;
    return null;
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    const reqError = validatePassword(password);
    setPasswordHint(reqError);
    if (reqError) return;
    if (!password || password !== confirmPassword) {
      setConfirmPasswordError("Passwords do not match");
      setError("Passwords do not match");
      return;
    }
    // Clear confirm password error if passwords match
    setConfirmPasswordError(null);
    const hasEncryptedParam = Boolean(params.get("data"));
    if (hasEncryptedParam && !decryptionReady) {
      setError("Please wait while we validate your invitation link.");
      return;
    }
    const effectiveToken = decryptedToken || tokenFromUrl;
    if (!effectiveToken) {
      setError("Invalid or missing confirmation token");
      return;
    }
    try {
      setLoading(true);
      const res = await userManagementService.confirmInvitation({
        confirmation_token: effectiveToken,
        password,
      });
      if (res?.success) {
        setSuccess("Invitation confirmed. You can sign in now.");
        setTimeout(() => navigate("/login"), 1200);
      } else {
        setError(res?.error?.message || res?.message || "Confirmation failed");
      }
    } catch (err: any) {
      setError(err?.response?.message || err?.message || "Confirmation failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <SignUpContainer>
      <ContactSection>
        <LeftPanel>
          <LeftPanelContent>
            <MainTitle>Welcome to UniComply</MainTitle>
            <SubTitle>
              Set a secure password to activate your account and get started.
            </SubTitle>
          </LeftPanelContent>
        </LeftPanel>

        <RightPanel>
          <Title>Set your password</Title>
          <Typography sx={{ mb: 2, color: "#065b70" }}>
            Please create a password to activate your account.
          </Typography>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}
          <Box component="form" onSubmit={onSubmit} sx={{ width: '100%' }}>
            <FormContainer>
            <InputContainer>
              <InputLabel>Enter New Password</InputLabel>
              <StyledTextField
                type="password"
                value={password}
                onChange={(e) => {
                  const newPassword = e.target.value;
                  setPassword(newPassword);
                  setPasswordHint(validatePassword(newPassword));
                  // Validate password match when password changes
                  if (confirmPassword && newPassword !== confirmPassword) {
                    setConfirmPasswordError("Passwords do not match");
                  } else if (confirmPassword && newPassword === confirmPassword) {
                    setConfirmPasswordError(null);
                  }
                }}
                placeholder="Enter your password"
                required
                fullWidth
              />
              {passwordHint && (
                <Typography variant="caption" color="error" sx={{ mt: 0 }}>
                  {passwordHint}
                </Typography>
              )}
            </InputContainer>

            <InputContainer>
              <InputLabel>Confirm New Password</InputLabel>
              <StyledTextField
                type="password"
                value={confirmPassword}
                onChange={(e) => {
                  const newConfirmPassword = e.target.value;
                  setConfirmPassword(newConfirmPassword);
                  // Validate password match
                  if (newConfirmPassword && password && newConfirmPassword !== password) {
                    setConfirmPasswordError("Passwords do not match");
                  } else {
                    setConfirmPasswordError(null);
                  }
                }}
                placeholder="Confirm your password"
                required
                fullWidth
                error={!!confirmPasswordError}
              />
              {confirmPasswordError && (
                <Typography variant="caption" color="error" sx={{ mt: 0 }}>
                  {confirmPasswordError}
                </Typography>
              )}
            </InputContainer>
            <Button
              type="submit"
              disabled={loading}
              sx={{
                background: "linear-gradient(90deg, #0a829d 0%, #35d7e4 100%)",
                border: "1px solid #ffffff",
                borderRadius: "60px",
                height: { xs: 52, sm: 60 },
                width: "100%",
                padding: "12px 24px",
                boxShadow:
                  "0px 4px 24px 0px rgba(10,130,157,0.2), 0px 4px 7.3px 0px rgba(133,233,255,0.4)",
                textTransform: "none",
                fontFamily: 'Rethink Sans, sans-serif',
                fontWeight: 600,
                fontSize: { xs: 16, sm: 18 },
                lineHeight: "22px",
                color: "#ffffff",
                minHeight: 44,
                '&:hover': {
                  background:
                    "linear-gradient(90deg, #086e86 0%, #2bc9d6 100%)",
                },
                mt: 1,
              }}
            >
              {loading ? "Submitting..." : "Continue"}
            </Button>
            <TermsContainer>
              <TermsText>
                
                <span style={{ color: "#737373" }}>By continuing, you agree to </span>
                <TermsLink onClick={() => navigate("/terms-of-service")}>
                  UniComply.ai's Terms of Service
                </TermsLink>
                {userType?.toLowerCase().includes("external") && (
                  <>
                  <span style={{ color: "#737373" }}>, </span>
                  </>
                )}
              </TermsText>
              <TermsText>
              {isStandardHubUserTypeName(userType) && (
                  <>
                   <span style={{ color: "#737373" }}> and </span>
                   
                  <TermsLink onClick={() => navigate("/privacy-policy")}>Privacy Policy</TermsLink>
                   
                  </>
                )}
                {userType?.toLowerCase().includes("external") && (
                  <>
                  <TermsLink onClick={() => navigate("/privacy-policy")}>Privacy Policy</TermsLink>
                    <span style={{ color: "#737373" }}> and </span>
                    <TermsLink onClick={() => navigate("")}>BAA</TermsLink>
                  </>
                )}
              </TermsText>
            </TermsContainer>
            </FormContainer>
          </Box>
        </RightPanel>
      </ContactSection>
    </SignUpContainer>
  );
}


